import { z } from 'zod';
/**
 * The movie wire contract.
 *
 * One shape serves the board card, the card back and the admin panel. There is
 * no "summary" variant: a movie row is a few hundred bytes, the board never
 * shows more than a handful at once, and a second shape is a second thing to
 * keep in step for no measurable saving.
 */
export const castMemberSchema = z.object({
    name: z.string(),
    /** TMDB has one for nearly everyone, but not always. */
    character: z.string().nullable(),
});
export const movieSchema = z.object({
    id: z.string().uuid(),
    tmdbId: z.number().int().nullable(),
    imdbId: z.string().nullable(),
    /**
     * Always Latin. Everything from TMDB passes through toSerbianLatin().
     *
     * **Nullable, and the null is the point.** It is null when TMDB has no
     * Serbian translation of this film, rather than holding the English title
     * TMDB hands back in its place — which is what makes a member's language
     * order able to fall through to the next one. See `titles.ts`.
     */
    titleSr: z.string().nullable(),
    /** TMDB's `en-US` title. Null when it has none, or before a re-import. */
    titleEn: z.string().nullable(),
    /** The film's own title, transliterated when it was Cyrillic. */
    titleOrig: z.string().nullable(),
    year: z.number().int().nullable(),
    runtimeMin: z.number().int().nullable(),
    director: z.string().nullable(),
    cast: z.array(castMemberSchema),
    synopsisSr: z.string().nullable(),
    /**
     * Relative paths under /media, never absolute URLs — PUBLIC_ORIGIN is one
     * env var away from changing and a table of absolute URLs would make that a
     * migration. Null is a real state: fixture mode has no art at all.
     */
    posterUrl: z.string().nullable(),
    backdropUrl: z.string().nullable(),
    imdbRating: z.number().nullable(),
    rtScore: z.number().int().nullable(),
    /** 'horor', 'spor', 'kult' — yours, not TMDB's genres. */
    tags: z.array(z.string()),
});
// --- admin: search and import ----------------------------------------------
/**
 * A TMDB result. Nothing here is in our database yet.
 *
 * Search asks TMDB once, in Serbian, so `titleEn` is only ever filled in when
 * that one response was enough to tell the two apart — see `searchMovies`. A
 * hit is a row in a list somebody is about to click, not a film; the full set
 * arrives with the import.
 */
export const movieSearchHitSchema = z.object({
    tmdbId: z.number().int(),
    titleSr: z.string().nullable(),
    titleEn: z.string().nullable(),
    titleOrig: z.string().nullable(),
    year: z.number().int().nullable(),
    /** TMDB's own URL, shown only in the admin list. Never stored. */
    posterUrl: z.string().nullable(),
    overviewSr: z.string().nullable(),
    /** Set when this tmdb_id is already a row — the button says "ponovo uvezi". */
    importedId: z.string().uuid().nullable(),
});
export const movieSearchResponse = z.object({
    /** False when no TMDB key is configured: results came from the fixture set. */
    live: z.boolean(),
    results: z.array(movieSearchHitSchema),
});
export const importMovieRequest = z.object({
    tmdbId: z.number().int().positive(),
});
/**
 * Hand overrides.
 *
 * These are stored in `movies.manual_overrides` and applied **at read time**,
 * on top of whatever the last import wrote. That is the whole point: a bad
 * Serbian title you fixed by hand, or a joke rating, has to survive the next
 * re-import of the same film. Merging them into the columns would mean the
 * re-import silently ate your correction.
 *
 * There is no poster override — art is managed by the media pipeline, not
 * typed in.
 */
export const movieOverrideValues = z
    .object({
    /**
     * Nullable like the column: "this film has no Serbian title" is a
     * correction somebody will want to make the first time the importer
     * guesses one wrong, and it is the only way to make a preference list fall
     * through to English by hand.
     */
    titleSr: z.string().trim().min(1).max(200).nullable(),
    titleEn: z.string().trim().max(200).nullable(),
    titleOrig: z.string().trim().max(200).nullable(),
    year: z.number().int().min(1870).max(2200).nullable(),
    runtimeMin: z.number().int().positive().max(1000).nullable(),
    director: z.string().trim().max(200).nullable(),
    synopsisSr: z.string().trim().max(4000).nullable(),
    imdbRating: z.number().min(0).max(10).nullable(),
    rtScore: z.number().int().min(0).max(100).nullable(),
    tags: z.array(z.string().trim().min(1).max(40)).max(12),
})
    .partial();
/** The overridable subset as data — the API applies them in this order and the
 * admin form renders one input per entry. */
export const OVERRIDABLE_FIELDS = [
    'titleSr',
    'titleEn',
    'titleOrig',
    'year',
    'runtimeMin',
    'director',
    'synopsisSr',
    'imdbRating',
    'rtScore',
    'tags',
];
const overridableField = z.enum([
    'titleSr',
    'titleEn',
    'titleOrig',
    'year',
    'runtimeMin',
    'director',
    'synopsisSr',
    'imdbRating',
    'rtScore',
    'tags',
]);
/**
 * `set` writes overrides; `clear` removes them and lets the imported value show
 * through again.
 *
 * They are two lists rather than one map with nulls because `null` already
 * means something here — "this film has no director" is a correction somebody
 * might deliberately make, and it must be distinguishable from "stop
 * overriding the director". JSON has no `undefined` to spend on the difference.
 */
export const movieOverrideRequest = z
    .object({
    set: movieOverrideValues.default({}),
    clear: z.array(overridableField).max(OVERRIDABLE_FIELDS.length).default([]),
})
    .partial();
//# sourceMappingURL=movies.js.map